// Starting-grid layout. Single source of truth for both:
//   - spawning cars at race start (main.js → spawnCarsOnGrid)
//   - rendering grid-box paint on the track (render.js → drawGridSlots)
//
// Layout math:
//   pole = startLine if authored, else startFinish.
//   Each row sits GRID_SPACING_M behind the previous, with the whole
//   stack offset GRID_BACK_OFFSET_M behind pole so cars have a run-up
//   to S/F (real grids never have pole AT the line — there's always a
//   short approach).
//   Lateral side alternates: pole +, P2 −, P3 +, ... (F1-style zigzag).

import { curvePointAt, curveHeadingAt } from '../../shared/spline.js';
import { nearestPointOnDense } from '../../shared/geometry.js';

// Distance between adjacent grid slots along the centerline. Car
// length is 4.6 m; 8 m leaves room between bumpers and lets the AI
// line-following pass find a path forward without rear-ending.
export const GRID_SPACING_M = 8;

// Lateral stagger from centerline. ±3 m fits inside any reasonable
// track strip half-width and gives ~0.5 m clear air between cars at
// the same row spacing.
export const GRID_LATERAL_OFFSET_M = 3;

// How far behind S/F pole sits. 10 m gives every car a short run-up
// and avoids the awkwardness of pole spawning ON the line.
export const GRID_BACK_OFFSET_M = 10;

// Compute one slot per car. Each slot is { x, y, heading } in world
// coords. Heading is along the car's forward direction.
export function computeGridSlots(track, geometry, slotCount) {
  const wps = track.trackline.waypoints;
  const alpha = track.trackline.alpha ?? 0.5;
  const direction = track.direction ?? 'cw';
  const headingFlip = direction === 'ccw' ? Math.PI : 0;

  const poleMarker = track.startLine || track.startFinish || { segmentIdx: 0, t: 0.35 };
  const polePos = curvePointAt(wps, poleMarker.segmentIdx, poleMarker.t, alpha);
  const poleTan = curveHeadingAt(wps, poleMarker.segmentIdx, poleMarker.t, alpha);

  const forwardX = Math.cos(poleTan + headingFlip);
  const forwardY = Math.sin(poleTan + headingFlip);

  const slots = [];
  for (let i = 0; i < slotCount; i++) {
    const forwardOffset = GRID_BACK_OFFSET_M + GRID_SPACING_M * i;
    const lateralSide = (i % 2 === 0) ? +1 : -1;
    const lateralOffset = lateralSide * GRID_LATERAL_OFFSET_M;

    const targetX = polePos.x - forwardX * forwardOffset;
    const targetY = polePos.y - forwardY * forwardOffset;

    let slotX = targetX, slotY = targetY, headingTan = poleTan;
    if (geometry.centerline?.length >= 2) {
      const np = nearestPointOnDense({ x: targetX, y: targetY }, geometry.centerline, true);
      if (np) {
        slotX = np.point.x;
        slotY = np.point.y;
        const a = geometry.centerline[np.segmentIdx];
        const b = geometry.centerline[(np.segmentIdx + 1) % geometry.centerline.length];
        headingTan = Math.atan2(b.y - a.y, b.x - a.x);
      }
    }

    const localForwardAngle = headingTan + headingFlip;
    const perpX = -Math.sin(localForwardAngle);
    const perpY =  Math.cos(localForwardAngle);
    slotX += perpX * lateralOffset;
    slotY += perpY * lateralOffset;

    slots.push({ x: slotX, y: slotY, heading: localForwardAngle });
  }
  return slots;
}
